import React from 'react';
import styled from 'styled-components';
import { ThemeColors, Spacing, Typography, BorderRadius } from '../../../styles/ThemeStyles';
import { Plus, Minus } from 'lucide-react';

interface ProductCardProps {
  product: {
    id: string;
    code: string;
    name: string;
    price?: number;
    retail?: string;
    category?: string;
    description?: string;
  };
  quantity: number;
  onQuantityChange: (productId: string, quantity: number) => void;
  isSelected?: boolean;
  showPrice?: boolean;
  emphasizePriceInRed?: boolean;
  showCategory?: boolean;
  isOutOfStock?: boolean;
}

const Card = styled.div<{ isSelected?: boolean }>`
  padding: ${Spacing.lg};
  border: 2px solid ${props => 
    props.isSelected ? ThemeColors.primary.main : 
    'rgba(76, 175, 80, 0.1)'};
  border-radius: ${BorderRadius.lg};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  background: ${props => 
    props.isSelected ? 'linear-gradient(135deg, #f8fffe 0%, #e8f5e8 100%)' : 
    'linear-gradient(135deg, #ffffff 0%, #f8fffe 100%)'};
  box-shadow: ${props => props.isSelected 
    ? '0 8px 24px rgba(76, 175, 80, 0.15), 0 4px 12px rgba(0, 0, 0, 0.08)' 
    : '0 4px 16px rgba(0, 0, 0, 0.06), 0 2px 8px rgba(0, 0, 0, 0.04)'};
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.isSelected 
      ? 'linear-gradient(90deg, #4CAF50, #388E3C)' 
      : 'transparent'};
    transition: all 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.isSelected 
      ? '0 12px 32px rgba(76, 175, 80, 0.2), 0 6px 16px rgba(0, 0, 0, 0.1)' 
      : '0 8px 24px rgba(76, 175, 80, 0.12), 0 4px 12px rgba(0, 0, 0, 0.06)'};
    border-color: ${props => props.isSelected 
      ? ThemeColors.primary.dark 
      : 'rgba(76, 175, 80, 0.2)'};
  }
`;

const ProductHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${Spacing.md};
`;

const ProductTitle = styled.div`
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.primary};
  flex: 1;
  line-height: 1.4;
  font-size: ${Typography.fontSize.lg};
  margin-right: ${Spacing.sm};
`;

const ProductPrice = styled.div`
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.primary.main};
  font-size: ${Typography.fontSize.xl};
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  
  &::before {
    content: '$';
    font-size: ${Typography.fontSize.sm};
    opacity: 0.7;
  }
`;

const ProductCode = styled.div`
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
  margin-bottom: ${Spacing.sm};
  font-family: 'Courier New', monospace;
  background: rgba(76, 175, 80, 0.1);
  padding: ${Spacing.xs} ${Spacing.sm};
  border-radius: ${BorderRadius.sm};
  display: inline-block;
  font-weight: ${Typography.fontWeight.medium};
`;

const ProductDescription = styled.div`
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
  line-height: 1.4;
  margin-bottom: ${Spacing.md};
`;

const CategoryBadge = styled.div`
  display: inline-block;
  background: linear-gradient(135deg, ${ThemeColors.primary.light}, ${ThemeColors.primary.main});
  color: white;
  padding: ${Spacing.xs} ${Spacing.sm};
  border-radius: ${BorderRadius.sm};
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.semiBold};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: ${Spacing.md};
  box-shadow: 0 2px 6px rgba(76, 175, 80, 0.2);
`;

const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  margin-top: ${Spacing.sm};
`;

const QuantityLabel = styled.span`
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  color: ${ThemeColors.text.primary};
`;

const QuantityInputGroup = styled.div`
  display: flex;
  align-items: center;
  border: 2px solid ${ThemeColors.border.light};
  border-radius: ${BorderRadius.sm};
  overflow: hidden;
`;

const QuantityButton = styled.button`
  background: ${ThemeColors.primary.main};
  color: white;
  border: none;
  padding: ${Spacing.xs};
  cursor: pointer;
  transition: all 0.3s ease;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: ${ThemeColors.primary.dark};
  }
  
  &:disabled {
    background: ${ThemeColors.gray[300]};
    cursor: not-allowed;
  }
`;

const QuantityInput = styled.input`
  border: none;
  padding: ${Spacing.xs} ${Spacing.sm};
  text-align: center;
  font-weight: ${Typography.fontWeight.semiBold};
  width: 60px;
  background: white;
  font-size: 16px; /* Prevent zoom on iOS - must be at least 16px */
  
  &:focus {
    outline: none;
  }
`;

export const ProductCard: React.FC<ProductCardProps> = ({
  product,
  quantity,
  onQuantityChange,
  isSelected = false,
  showPrice = true,
  showCategory = false
}) => {
  const handleQuantityChange = (newQuantity: number) => {
    onQuantityChange(product.id, newQuantity);
  };

  const formatPrice = () => {
    if (product.price !== undefined) {
      return product.price.toFixed(2);
    }
    if (product.retail) {
      return product.retail.replace('$', '');
    }
    return '0.00';
  };

  const formatProductName = (name: string) => {
    // Handle multi-line names (English/French format)
    return name.split('\n').map((line, index) => (
      <div key={index}>{line}</div>
    ));
  };

  return (
    <Card isSelected={isSelected}>
      <ProductHeader>
        <ProductTitle>
          {formatProductName(product.name)}
        </ProductTitle>
        {showPrice && (
          <ProductPrice>
            {formatPrice()}
          </ProductPrice>
        )}
      </ProductHeader>
      
      <ProductCode>Code: {product.code}</ProductCode>
      
      {product.description && (
        <ProductDescription>
          {formatProductName(product.description)}
        </ProductDescription>
      )}
      
      {showCategory && product.category && (
        <CategoryBadge>
          {product.category}
        </CategoryBadge>
      )}
      
      <QuantityControls>
        <QuantityLabel>Qty:</QuantityLabel>
        <QuantityInputGroup>
          <QuantityButton
            onClick={() => handleQuantityChange(Math.max(0, quantity - 1))}
            disabled={quantity <= 0}
          >
            <Minus size={16} />
          </QuantityButton>
          <QuantityInput
            type="number"
            inputMode="numeric"
            pattern="[0-9]*"
            min="0"
            value={quantity}
            onChange={(e) => handleQuantityChange(parseInt(e.target.value) || 0)}
          />
          <QuantityButton
            onClick={() => handleQuantityChange(quantity + 1)}
          >
            <Plus size={16} />
          </QuantityButton>
        </QuantityInputGroup>
      </QuantityControls>
    </Card>
  );
};

export default ProductCard;
